import { CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { MDXRemote } from "next-mdx-remote";
import { useCallback, useEffect, useState } from "react";
import { convertMDX } from "utils/mdx";

import {
  CustomSelect,
  StyledOption,
} from "@/components/onboardingSections/inputs";
import styles from "@/styles/fx.module.scss";

const Fx = ({ data }) => {
  const router = useRouter();
  const [currency, setCurrency] = useState("AUD");
  const [title, setTitle] = useState(null);
  const [description, setDescription] = useState(null);
  const [rate, setRate] = useState([]);
  const [loading, setLoading] = useState(true);
  const dateOptions = { year: "numeric", month: "long", day: "numeric" };

  const setMDX = useCallback(async (source, title) => {
    const mdxSource = await convertMDX(source, false);
    if (title) setTitle(mdxSource);
    else setDescription(mdxSource);
  }, []);

  const getRate = useCallback(
    async selectedCurrency => {
      setLoading(true);
      setCurrency(selectedCurrency);
      try {
        const fxresponse = await fetch(`/api/fx`, {
          method: "POST",
          body: JSON.stringify({
            type: "CREDIT_CARD",
            transactionAmount: 1,
            transactionCurrency: selectedCurrency,
          }),
        });
        const fxres =
          fxresponse.status === 500 ? false : await fxresponse.json();
        setRate(fxres.data[1]);
        setLoading(false);
      } catch {
        router.push("/500");
      }
    },
    [router],
  );

  useEffect(() => {
    getRate("AUD");
    setMDX(data.title.replace("<br>", " "), true);
    setMDX(data.description.replace("<br>", " "));
  }, [getRate, setMDX, data.title, data.description]);

  const variants = {
    open: { height: 54 },
    closed: { height: 50 },
  };

  const mobileVariants = {
    open: { height: 47 },
    closed: { height: 43 },
  };

  return (
    <div className="py-12 md:py-20 max-w-[900px] px-5 md:px-7 mx-auto flex flex-col gap-10">
      {title === null ? null : (
        <div className={styles.overview}>
          <MDXRemote {...title} />
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        <h5 className={styles.subTitle}>Pick a currency</h5>
        <label htmlFor="currencyOptions" className={styles.inputTitle}>
          Choose a currency from the options listed below:
        </label>
        <div className="relative w-full md:w-[400px]">
          <CustomSelect
            id="currencyOptions"
            value={currency}
            onChange={getRate}
          >
            {data.currency.map((v, i) => (
              <StyledOption key={i} value={v.slice(0, 3)}>
                {v}
              </StyledOption>
            ))}
          </CustomSelect>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <h5 className={styles.subTitle}>Rates and overseas fees:</h5>
        <div className="flex flex-col">
          <div className="hidden md:flex gap-1.5 items-end">
            <AnimatePresence>
              <motion.button
                key="credit"
                variants={variants}
                className={`bg-onmoPrimary ${styles.containerTitle}`}
              >
                Onmo Credit Card
              </motion.button>
            </AnimatePresence>
          </div>
          <div className="flex md:hidden gap-1.5 items-end">
            <AnimatePresence>
              <motion.button
                key="credit"
                variants={mobileVariants}
                className={`bg-onmoPrimary ${styles.containerTitle}`}
              >
                Credit Card
              </motion.button>
            </AnimatePresence>
          </div>
          <div className={`border-t-4 border-onmoPrimary ${styles.container}`}>
            <div className="px-7 pt-8 md:py-8 flex flex-col gap-8 md:gap-12">
              <div className="flex flex-col gap-2.5">
                <p className={styles.containerSub}>
                  European Central Bank rate
                </p>
                <p className={`text-onmoPrimary ${styles.containerInfo}`}>
                  1 GBP ={" "}
                  {loading ? (
                    <CircularProgress
                      className="!text-onmoPrimary"
                      thickness={6}
                      size={15}
                      sx={{ marginLeft: "5px" }}
                    />
                  ) : (
                    `${rate.rateCompareResult.ecbRate} ${" "} ${currency}`
                  )}
                </p>
                <p className={styles.containerDate}>
                  Date updated:{" "}
                  {loading ? (
                    <CircularProgress
                      thickness={6}
                      size={15}
                      sx={{ color: "#667085", marginLeft: "5px" }}
                    />
                  ) : (
                    new Date(
                      rate.rateCompareResult.rateDate,
                    ).toLocaleDateString("en-GB", dateOptions)
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <p className={styles.containerSub}>
                  Total mark-up over European Central Bank rate
                </p>
                <p className={`text-onmoPrimary ${styles.containerInfo}`}>
                  {loading ? (
                    <CircularProgress
                      className="!text-onmoPrimary"
                      thickness={6}
                      size={15}
                      sx={{ marginRight: "5px" }}
                    />
                  ) : (
                    `${rate.rateCompareResult.partnerMarkupOverEcb}`
                  )}
                  %
                </p>
              </div>
            </div>
            <div className="px-7 py-8 flex flex-col gap-8 md:gap-12">
              <div className="flex flex-col gap-2.5">
                <p className={styles.containerSub}>Visa rate</p>
                <p className={`text-onmoPrimary ${styles.containerInfo}`}>
                  1 GBP ={" "}
                  {loading ? (
                    <CircularProgress
                      className="!text-onmoPrimary"
                      thickness={6}
                      size={15}
                      sx={{ marginLeft: "5px" }}
                    />
                  ) : (
                    `${rate.rateCompareResult.ecbRate} ${" "} ${currency}`
                  )}
                </p>
                <p className={styles.containerDate}>
                  Date updated:{" "}
                  {loading ? (
                    <CircularProgress
                      thickness={6}
                      size={15}
                      sx={{ color: "#667085", marginLeft: "5px" }}
                    />
                  ) : (
                    new Date(
                      rate.rateCompareResult.rateDate,
                    ).toLocaleDateString("en-GB", dateOptions)
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <p className={styles.containerSub}>
                  Onmo mark-up over Visa rate
                </p>
                <p className={`text-onmoPrimary ${styles.containerInfo}`}>
                  {loading ? (
                    <CircularProgress
                      className="!text-onmoPrimary"
                      thickness={6}
                      size={15}
                      sx={{ marginRight: "5px" }}
                    />
                  ) : (
                    `${rate.rateCompareResult.partnerMarkupOverEcb}`
                  )}
                  %
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.description}>
          {description === null ? null : <MDXRemote {...description} />}
        </div>
      </div>
    </div>
  );
};

export default Fx;
